import { useEffect, useState } from "react"
import { FileText } from "lucide-react"
import { toast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { api } from "@/lib/api"

const field = "w-full rounded-xl border border-[#1B5E35]/25 bg-[#0A0F0B] px-4 py-3 text-sm text-[#F4F6F0] placeholder:text-[#6B7C6E] focus:border-[#1B5E35] focus:outline-none"
const EMPTY = { name: "", company: "", email: "", phone: "", location: "", notes: "" }

function Field({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-[#6B7C6E]">
      {label}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}

export function QuoteButton({ items, className = "", testid = "request-quote-btn" }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [lines, setLines] = useState(items)
  const [sending, setSending] = useState(false)
  const [doneRef, setDoneRef] = useState("")

  useEffect(() => { if (open) { setLines(items); setDoneRef("") } }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  const setQty = (i, q) => setLines(lines.map((l, j) => (j === i ? { ...l, quantity: Math.max(1, q || 1) } : l)))

  const submit = async (e) => {
    e.preventDefault()
    setSending(true)
    try {
      const { data } = await api.post("/quotes", { ...form, items: lines })
      setDoneRef(data.ref)
      setForm(EMPTY)
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details (a valid email is required)." : "Could not send your request. Please try again.")
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className={`inline-flex items-center justify-center gap-2 rounded-xl border border-[#1B5E35]/40 px-7 py-3 font-bold text-[#F4F6F0] transition-colors hover:border-[#2A7D4A] hover:bg-[#1B5E35]/10 ${className}`}>
        <FileText className="h-4 w-4" /> Request a Quote
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-[#1B5E35]/30 bg-[#111A14] text-[#F4F6F0]" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black text-[#F4F6F0]">REQUEST A QUOTE</DialogTitle>
            <DialogDescription className="text-[#6B7C6E]">Ordering in bulk? Tell us what you need and we'll reply with project pricing, delivery and installation options.</DialogDescription>
          </DialogHeader>
          {doneRef ? (
            <div className="py-6 text-center" data-testid="quote-success">
              <div className="font-mono text-xs tracking-widest text-[#2A7D4A]">REQUEST RECEIVED</div>
              <p className="mt-3 text-lg font-bold">Thank you! Your reference is <span data-testid="quote-ref">{doneRef}</span>.</p>
              <p className="mt-2 text-sm text-[#6B7C6E]">Our team will contact you within one business day.</p>
              <button type="button" onClick={() => setOpen(false)} className="mt-6 rounded-xl bg-[#1B5E35] px-7 py-3 font-bold transition-colors hover:bg-[#2A7D4A]">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2 rounded-xl border border-[#1B5E35]/20 bg-[#0A0F0B] p-3">
                {lines.map((l, i) => (
                  <div key={l.name} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-[#F4F6F0]/85">{l.name}</span>
                    <input type="number" min="1" value={l.quantity} onChange={(e) => setQty(i, Number(e.target.value))} aria-label={`Quantity for ${l.name}`} data-testid={`quote-qty-${i}`} className="w-24 rounded-lg border border-[#1B5E35]/25 bg-[#111A14] px-2 py-1.5 text-right" />
                  </div>
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name *"><input required value={form.name} onChange={set("name")} className={field} data-testid="quote-name" /></Field>
                <Field label="Company"><input value={form.company} onChange={set("company")} className={field} data-testid="quote-company" /></Field>
                <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="quote-email" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={field} data-testid="quote-phone" /></Field>
              </div>
              <Field label="Delivery location"><input value={form.location} onChange={set("location")} placeholder="e.g. Kampala, Uganda" className={field} data-testid="quote-location" /></Field>
              <Field label="Notes"><textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Installation, timelines, specifications…" className={field} data-testid="quote-notes" /></Field>
              <button type="submit" disabled={sending} data-testid="quote-submit" className="w-full rounded-xl bg-[#1B5E35] px-6 py-4 font-bold transition-colors hover:bg-[#2A7D4A] disabled:opacity-60">
                {sending ? "Sending…" : "Send quote request"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
